import { useState, useEffect } from 'react';
import { CurrencyCode } from '../config/currency';
import { OrganizationStorageService } from '../services/storage/OrganizationStorageService';
import { organizationApi } from '../services/api/endpoints/organization';

export interface Organization {
  id: string;
  name: string;
  entrepreneurName: string;     // Nom de l'entrepreneur
  associates?: string;          // Autres associés
  registrationNumber: string;   // RCCM
  taxId: string;                // Numéro impôt
  cnssNumber?: string;          // Numéro affiliation CNSS
  inppNumber?: string;          // Numéro affiliation INPP
  onemNumber?: string;          // Numéro affiliation ONEM
  address: string;
  city: string;
  country: string;
  phone: string;                // Téléphone entreprise
  entrepreneurPhone?: string;   // Téléphone entrepreneur
  email: string;                // Email entreprise
  entrepreneurEmail?: string;   // Email entrepreneur
  website: string;
  legalForm: string;
  capital: string;
  currency: CurrencyCode;
  logo?: string;
  industry?: string;            // Secteur d'activité
  description?: string;
}

export function useOrganization() {
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);

  // Charger les données de l'organisation depuis l'API
  useEffect(() => {
    const loadOrganization = async () => {
      try {
        setLoading(true);
        console.log('🔄 Chargement de l\'organisation depuis l\'API...');
        
        // Initialiser le service de stockage
        await OrganizationStorageService.init();
        
        // Appel API pour récupérer l'organisation
        const response = await organizationApi.getOrganization();
        console.log('📥 Réponse API getOrganization:', response);
        
        if (response.success && response.data) {
          console.log('✅ Organisation chargée avec succès');
          setOrganization(response.data);
          
          // Sauvegarder dans IndexedDB comme cache
          await OrganizationStorageService.saveOrganization(response.data);
        } else {
          console.warn('⚠️ API n\'a pas retourné de données, tentative de récupération depuis le cache');
          
          // Fallback vers IndexedDB uniquement (pas de mock data)
          const org = await OrganizationStorageService.getOrganization();
          
          if (org) {
            console.log('✅ Organisation récupérée depuis le cache');
            setOrganization(org);
          } else {
            console.log('ℹ️ Aucune organisation trouvée - état vide');
            setOrganization(null);
          }
        }
      } catch (error) {
        console.error('❌ Erreur lors du chargement:', error);
        
        // Fallback complet vers IndexedDB en cas d'erreur API
        try {
          console.log('💾 Fallback: chargement depuis IndexedDB');
          const org = await OrganizationStorageService.getOrganization();
          
          if (org) {
            console.log('✅ Organisation récupérée depuis le cache');
            setOrganization(org);
          } else {
            console.log('ℹ️ Aucune organisation trouvée - état vide');
            setOrganization(null);
          }
        } catch (fallbackError) {
          console.error('❌ Échec du fallback:', fallbackError);
          setOrganization(null);
        }
      } finally {
        setLoading(false);
      }
    };

    loadOrganization();
  }, []);

  // Mettre à jour l'organisation (ou créer si elle n'existe pas)
  const updateOrganization = async (data: Partial<Organization>) => {
    // Si pas d'organisation existante, créer une nouvelle
    const isCreating = !organization;
    
    try {
      console.log('🔄 Mise à jour de l\'organisation via API:', data);
      
      // Appel API pour mettre à jour l'organisation
      const response = await organizationApi.updateOrganization(data);
      console.log('📥 Réponse API updateOrganization:', response);
      
      if (response.success && response.data) {
        console.log('✅ Organisation mise à jour avec succès');
        setOrganization(response.data);
        
        // Mettre à jour le cache IndexedDB
        await OrganizationStorageService.updateOrganization(data);
        
        return response.data;
      } else {
        console.error('❌ Échec de la mise à jour:', response.error);
        throw new Error(response.error || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('❌ Erreur lors de la mise à jour:', error);
      
      // Fallback vers IndexedDB
      try {
        console.log('💾 Fallback: mise à jour dans IndexedDB uniquement');
        const updatedOrg = await OrganizationStorageService.updateOrganization(data);
        if (updatedOrg) {
          setOrganization(updatedOrg);
          return updatedOrg;
        }
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
      }
      
      throw error;
    }
  };

  return {
    organization,
    loading,
    updateOrganization
  };
}